import { User } from 'firebase/auth';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DocumentSidebar from "@/components/Documents";
import ToolsToggleSection from '@/components/ToolsToggleSection'
import EmissaryListMinimal from '@/components/EmissaryListMinimal';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenuButton,
  SidebarMenu,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarGroupAction,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Settings, 
  List, 
  ArrowLeft,
  Search,
  Edit,
  Copy,
  MessageSquare,
  Users,
  ChevronDown,
  ChevronUp,
  History,
  Mail
} from 'lucide-react';
import type { Document, UserState } from '@/types';
import Image from 'next/image';
import MultivacLogo from './MultivacLogo';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
// Import the AVAILABLE_TOOLS from ToolSelector
import { AVAILABLE_TOOLS } from './tools/ToolSelector';
import FirebaseService, { type Tag, type AccessControl } from '@/lib/firebase';
import { getUserAssignedTags } from '@/lib/access-control';
import { EmailSubscriptionDialog } from './EmailSubscriptionDialog';

interface AppSidebarProps {
  botName: string;
  currentBotAvatar: string;
  senderName: string;
  userState: UserState;
  currentUser: User | null;
  documents: Document[];
  onShowLogin: () => void;
  onLogout: () => void;
  editingBotId?: string;
  isAdminPage?: boolean;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteDocument?: (index: number) => void;
  isUploading?: boolean;
  // Add new props for tool configuration
  tools?: string[];
  toolConfigs?: Record<string, Record<string, any>>;
  onBackToLanding?: () => void;
  // Added shareId prop to support edit button functionality
  shareId?: string;
  onToolsChange?: (updatedTools: string[]) => void;
  // Add access control props for email subscriptions
  accessControl?: AccessControl;
  ownerEmail?: string;
  emailSubscriptions?: string[];
}

function getUserDisplayName(user: User | null): string {
  if (!user) return 'User';
  return user.displayName || user.email?.split('@')[0] || 'User';
}

function getUserInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

export function AppSidebar({
  botName,
  currentBotAvatar,
  senderName,
  userState,
  currentUser,
  documents,
  fileInputRef,
  onShowLogin,
  onLogout,
  onFileUpload,
  onDeleteDocument,
  editingBotId,
  isAdminPage = false,
  isUploading = false,
  tools = [],
  toolConfigs = {},
  onBackToLanding,
  shareId,
  onToolsChange,
  accessControl,
  ownerEmail,
  emailSubscriptions,
}: AppSidebarProps) {

  const router = useRouter();
  const pathname = usePathname();
  const displayName = getUserDisplayName(currentUser);
  const initials = getUserInitials(displayName);
  const [conversationsOpen, setConversationsOpen] = useState(true);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);

  // Load tags and compute user's assigned tags
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await FirebaseService.getAllTags();
        setAvailableTags(tags);
        
        // Get user's assigned tags
        const assignedTags = getUserAssignedTags(tags, currentUser);
        setUserTags(assignedTags);
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };

    loadTags();
  }, [currentUser]);
  
  const handleToolsChange = (updatedTools: string[]) => {
    if (onToolsChange) {
      onToolsChange(updatedTools);
    }
  };

  const handleEditAssistant = () => {
    if (shareId && currentUser) {
      router.push(`/admin?edit=${shareId}`);
    }
  };
  
  const showEditOptions = !isAdminPage && userState !== 'not-logged-in' && shareId;
  
  return (
    <Sidebar>
      <SidebarHeader className="border-b">
        {/* Logo */}
        <div className="px-2 py-2">
          <a href="/">
            <Image 
              src="/images/logo/animated-aitana.svg" 
              alt="Aitana" 
              width={160}
              height={40} 
            />
          </a>
        </div>

        {/* Section 1: Assistant Features */}
        <SidebarGroup className="pb-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={currentBotAvatar} alt={botName} />
              <AvatarFallback className="text-sm">{botName?.[0] || '?'}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm truncate">{botName || 'Assistant'}</h3>
              <p className="text-xs text-muted-foreground">Aitana Chat</p>
            </div>
            {/* Edit button inline with tools config */}
            {showEditOptions && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                onClick={handleEditAssistant}
              >
                <Edit className="h-3 w-3" />
              </Button>
            )}
            {/* Email dialog button */}
            {shareId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                onClick={() => setSubscriptionDialogOpen(true)}
                title="Email this assistant & manage subscriptions"
              >
                <Mail className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          {/* Compact Tools - Next to edit button */}
          {!isAdminPage && userState !== 'not-logged-in' && tools.length > 0 && (
            <SidebarGroupContent className="mt-2">
              <div className="text-xs text-muted-foreground mb-1">Tools: {tools.length} enabled</div>
              <ToolsToggleSection
                enabledTools={tools}
                onToolsChange={handleToolsChange}
                readOnly={false}
                userHasEditPermission={true}
                sessionOnly={true}
              />
            </SidebarGroupContent>
          )}
        </SidebarGroup>

        {/* Section 2: Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Back to Home */}
              {onBackToLanding && (
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={onBackToLanding} size="sm">
                    <ArrowLeft />
                    Back to Home
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Primary Action - Create Assistant */}
              {!isAdminPage && userState !== 'not-logged-in' && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="bg-highlight hover:bg-highlight/90 text-white">
                    <Link href="/create">
                      <Edit />
                      Create Assistant
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Secondary Navigation */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild variant="outline">
                  <Link href="/list">
                    <List />
                    View Assistants
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {!isAdminPage && userState !== 'not-logged-in' && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link href="/file-history">
                        <History />
                        File History
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link href="/admin">
                        <Settings />
                        Admin Panel
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link href="/">
                        <Users />
                        All Assistants
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarHeader>

      <SidebarContent>
        {/* Section 3: Recent Conversations - Collapsible */}
        {currentUser && (
          <Collapsible open={conversationsOpen} onOpenChange={setConversationsOpen}>
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger className="w-full flex items-center justify-between hover:bg-sidebar-accent">
                  Recent Conversations
                  {conversationsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <EmissaryListMinimal 
                    userId={currentUser.uid}
                    maxItems={8}
                    isCollapsible={false}
                  />
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
        
        {/* Documents Section */}
        {!isAdminPage && (
          <SidebarGroup>
            <DocumentSidebar 
              documents={documents}
              userState={userState}
            />
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        {/* Section 4: User Section */}
        {userState === 'not-logged-in' ? (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton 
                    onClick={onShowLogin}
                    className="bg-highlight hover:bg-highlight/90 text-white"
                  >
                    <Settings />
                    Log in
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <div className="flex items-center gap-2 px-2">
              <Avatar className="h-8 w-8">
                {currentUser?.photoURL ? (
                  <AvatarImage 
                    src={currentUser.photoURL} 
                    alt={displayName}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <AvatarFallback className="text-xs">
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-medium truncate text-sm">
                  {displayName}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {currentUser?.email || ''}
                </span>
              </div>
            </div>

            {/* User Tags Display */}
            {userTags.length > 0 && (
              <div className="px-2 py-2">
                <div className="text-xs text-muted-foreground mb-1">Access Level:</div>
                <div className="flex flex-wrap gap-1">
                  {userTags.map(tagId => {
                    const tag = availableTags.find(t => t.id === tagId);
                    const tagName = tag?.name || tagId;
                    
                    // Different colors for different tag types
                    const getTagColor = (name: string) => {
                      if (name === 'admin-tools') return 'bg-red-100 text-red-800 border-red-200';
                      if (name === 'beta-testers') return 'bg-purple-100 text-purple-800 border-purple-200';
                      if (name === 'finance-team') return 'bg-green-100 text-green-800 border-green-200';
                      if (name === 'ONE') return 'bg-blue-100 text-blue-800 border-blue-200';
                      return 'bg-gray-100 text-gray-800 border-gray-200';
                    };
                    
                    return (
                      <Badge 
                        key={tagId}
                        variant="outline" 
                        className={`text-xs py-0 px-1 h-5 ${getTagColor(tagName)}`}
                      >
                        {tagName}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <div className="flex gap-1">
                    <SidebarMenuButton asChild className="flex-1" size="sm">
                      <Link href="/feed">
                        <MessageSquare />
                        Feed
                      </Link>
                    </SidebarMenuButton>
                    <SidebarMenuButton onClick={onLogout} className="flex-1" size="sm">
                      Log Out
                    </SidebarMenuButton>
                  </div>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        <div className="px-2">
          <MultivacLogo />
        </div>
      </SidebarFooter>

      {/* Email Subscription Dialog */}
      {shareId && (
        <EmailSubscriptionDialog
          isOpen={subscriptionDialogOpen}
          onClose={() => setSubscriptionDialogOpen(false)}
          assistantId={shareId}
          assistantName={botName}
          currentUser={currentUser}
          accessControl={accessControl}
          ownerEmail={ownerEmail}
          emailSubscriptions={emailSubscriptions}
        />
      )}
    </Sidebar>
  );
}

export default AppSidebar;